import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from "recharts";
import { BarChart3, Users, Clock, TrendingUp, CalendarCheck, Loader2 } from "lucide-react";

const COLORS = ["#6A1E2E", "#E55934", "#1E4620", "#2563EB", "#7C3AED", "#B8860B", "#0D9488", "#BE185D"];

const Analytics = () => {
  const { getAuthHeaders } = useAuth();
  const [overview, setOverview] = useState(null);
  const [hoursByEmployee, setHoursByEmployee] = useState([]);
  const [shiftsByPosition, setShiftsByPosition] = useState([]);
  const [weeklyTrends, setWeeklyTrends] = useState([]);
  const [requestStats, setRequestStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState("month");
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    end: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });

  useEffect(() => {
    fetchAll();
  }, [dateRange]);

  const handlePeriodChange = (period) => {
    setPeriodType(period);
    const today = new Date();
    if (period === "week") {
      setDateRange({
        start: format(startOfWeek(today, { weekStartsOn: 0 }), "yyyy-MM-dd"),
        end: format(endOfWeek(today, { weekStartsOn: 0 }), "yyyy-MM-dd"),
      });
    } else if (period === "month") {
      setDateRange({
        start: format(startOfMonth(today), "yyyy-MM-dd"),
        end: format(endOfMonth(today), "yyyy-MM-dd"),
      });
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const opts = { headers };
      const [ovRes, hrRes, posRes, trendRes, reqRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, opts),
        axios.get(`${API}/analytics/hours-by-employee?start_date=${dateRange.start}&end_date=${dateRange.end}`, opts),
        axios.get(`${API}/analytics/shifts-by-position?start_date=${dateRange.start}&end_date=${dateRange.end}`, opts),
        axios.get(`${API}/analytics/weekly-trends?weeks=8`, opts),
        axios.get(`${API}/analytics/request-stats`, opts),
      ]);
      setOverview(ovRes.data);
      setHoursByEmployee(hrRes.data);
      setShiftsByPosition(posRes.data);
      setWeeklyTrends(trendRes.data);
      setRequestStats(reqRes.data);
    } catch (e) {
      console.error("Analytics fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#6A1E2E]" />
      </div>
    );
  }

  const pieData = shiftsByPosition.map(p => ({ name: p.position, value: p.total }));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Analytics</h1>
          <p className="text-[#666666]">Workforce insights and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodType} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-36 border-[#E5E5E5]" data-testid="analytics-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          {periodType === "custom" && (
            <div className="flex gap-2">
              <Input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="border-[#E5E5E5] w-36" />
              <Input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="border-[#E5E5E5] w-36" />
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#F0EAEB] flex items-center justify-center"><Users className="w-5 h-5 text-[#6A1E2E]" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{overview.total_employees}</p>
                  <p className="text-xs text-[#666666] uppercase tracking-wide">Employees</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#FEE2E2] flex items-center justify-center"><CalendarCheck className="w-5 h-5 text-[#E55934]" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{overview.total_shifts}</p>
                  <p className="text-xs text-[#666666] uppercase tracking-wide">Total Shifts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-orange-50 flex items-center justify-center"><Clock className="w-5 h-5 text-[#B8860B]" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#E55934]">{overview.open_shifts}</p>
                  <p className="text-xs text-[#666666] uppercase tracking-wide">Open Shifts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-[#1E4620]" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1E4620]">{overview.fill_rate}%</p>
                  <p className="text-xs text-[#666666] uppercase tracking-wide">Fill Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hours by Employee */}
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#6A1E2E]" /> Hours by Employee
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hoursByEmployee.length === 0 ? (
              <p className="text-sm text-[#999999] py-8 text-center">No shift data for this period</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hoursByEmployee} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#666" }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#666" }} />
                  <Tooltip contentStyle={{ borderRadius: "4px", border: "1px solid #E5E5E5" }} />
                  <Legend />
                  <Bar dataKey="regular" stackId="a" fill="#6A1E2E" name="Regular" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="overtime" stackId="a" fill="#E55934" name="Overtime" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Shifts by Position */}
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-[#6A1E2E]" /> Shifts by Position
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <p className="text-sm text-[#999999] py-8 text-center">No shift data for this period</p>
            ) : (
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Weekly Trends */}
      <Card className="border border-[#E5E5E5] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#6A1E2E]" /> Weekly Trends (Last 8 Weeks)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {weeklyTrends.length === 0 ? (
            <p className="text-sm text-[#999999] py-8 text-center">No trend data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={weeklyTrends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#666" }} />
                <YAxis tick={{ fontSize: 11, fill: "#666" }} />
                <Tooltip contentStyle={{ borderRadius: "4px", border: "1px solid #E5E5E5" }} />
                <Legend />
                <Area type="monotone" dataKey="filled_shifts" stroke="#6A1E2E" fill="#6A1E2E" fillOpacity={0.15} name="Filled Shifts" />
                <Area type="monotone" dataKey="open_shifts" stroke="#E55934" fill="#E55934" fillOpacity={0.15} name="Open Shifts" />
                <Line type="monotone" dataKey="overtime_hours" stroke="#B8860B" strokeWidth={2} name="Overtime Hrs" dot={{ fill: "#B8860B" }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Request Approval Stats */}
      {requestStats && (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Request Approval Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Trades", data: requestStats.trades, color: "#6A1E2E" },
                { label: "Drops", data: requestStats.drops, color: "#E55934" },
                { label: "Pickups", data: requestStats.pickups, color: "#1E4620" },
                { label: "Time Off", data: requestStats.time_off, color: "#2563EB" },
              ].map(item => (
                <div key={item.label} className="p-4 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm">
                  <p className="text-xs uppercase tracking-wider text-[#666666] mb-1">{item.label}</p>
                  <p className="text-2xl font-bold" style={{ color: item.color }}>{item.data.rate}%</p>
                  <p className="text-xs text-[#999999]">{item.data.approved}/{item.data.total} approved</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
